import { useState } from 'react';
import { X, Calendar, AlertTriangle, CheckCircle } from 'lucide-react';
import { timeOffService, TimeOffType, ScheduleImpact } from '../../services/time-off.service';

interface RequestTimeOffModalProps {
  agentId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function RequestTimeOffModal({
  agentId,
  onClose,
  onSuccess,
}: RequestTimeOffModalProps) {
  const [type, setType] = useState<TimeOffType>('VACATION');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [impact, setImpact] = useState<ScheduleImpact | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const timeOffTypes: Array<{ value: TimeOffType; label: string; description: string }> = [
    { value: 'VACATION', label: 'Vacation', description: 'Planned time off for leisure' },
    { value: 'SICK', label: 'Sick Leave', description: 'Illness or medical appointments' },
    { value: 'PERSONAL', label: 'Personal Day', description: 'Personal matters' },
    { value: 'BEREAVEMENT', label: 'Bereavement', description: 'Family loss' },
    { value: 'UNPAID', label: 'Unpaid Leave', description: 'Time off without pay' },
  ];

  const calculateDays = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const handleAnalyzeImpact = async () => {
    if (!startDate || !endDate) return;

    setIsAnalyzing(true);
    try {
      const impactData = await timeOffService.analyzeTimeOffImpact(
        agentId,
        startDate,
        endDate
      );
      setImpact(impactData);
    } catch (error) {
      console.error('Failed to analyze impact:', error);
      // Use demo impact data
      setImpact({
        affectedShifts: calculateDays(),
        coverageGap: Math.random() > 0.7,
        understaffedHours: Math.floor(Math.random() * 16),
        channelsAffected: ['Phone Support', 'Email Support'],
        skillsRequired: ['Customer Service', 'Technical Support'],
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      alert('Please select start and end dates');
      return;
    }

    setIsSubmitting(true);
    try {
      await timeOffService.createTimeOffRequest(agentId, {
        type,
        startDate,
        endDate,
        reason: reason || undefined,
      });
      onSuccess();
    } catch (error) {
      console.error('Failed to create time off request:', error);
      alert('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalDays = calculateDays();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Request Time Off</h2>
            <p className="text-sm text-gray-600 mt-1">
              Submit a request for paid or unpaid time off
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {/* Time Off Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Off Type
            </label>
            <div className="grid grid-cols-1 gap-2">
              {timeOffTypes.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setType(option.value)}
                  className={`p-3 border-2 rounded-lg text-left transition-all ${
                    type === option.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{option.label}</p>
                      <p className="text-sm text-gray-600">{option.description}</p>
                    </div>
                    {type === option.value && (
                      <CheckCircle className="h-5 w-5 text-blue-600" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setImpact(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setImpact(null);
                }}
                min={startDate}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Total Days */}
          {totalDays > 0 && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-900">
                  Total Days: {totalDays}
                </span>
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Provide additional context for your request..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Analyze Impact Button */}
          {startDate && endDate && !impact && (
            <button
              onClick={handleAnalyzeImpact}
              disabled={isAnalyzing}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-medium disabled:opacity-50"
            >
              {isAnalyzing ? 'Analyzing...' : 'Preview Schedule Impact'}
            </button>
          )}

          {/* Impact Analysis */}
          {impact && (
            <div
              className={`p-4 rounded-lg border ${
                impact.coverageGap
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-green-50 border-green-200'
              }`}
            >
              <div className="flex items-start space-x-3">
                {impact.coverageGap ? (
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <h4
                    className={`font-semibold ${
                      impact.coverageGap ? 'text-yellow-900' : 'text-green-900'
                    }`}
                  >
                    {impact.coverageGap ? 'Coverage Gap Detected' : 'Minimal Impact'}
                  </h4>
                  <ul className="mt-2 space-y-1 text-sm text-gray-700">
                    <li>• {impact.affectedShifts} shifts will need coverage</li>
                    {impact.understaffedHours > 0 && (
                      <li>• {impact.understaffedHours} hours may be understaffed</li>
                    )}
                    <li>• Channels affected: {impact.channelsAffected.join(', ')}</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!startDate || !endDate || isSubmitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  );
}
